import { cyan, purple } from '@bloomfieldrobotics/canopy';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
  BlueberryFeatures,
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../../services/data/impl/cube/cubeSlice';
import {
  isBlueberryFeature,
  isBlueberryFlowerFeature,
} from '../../../../services/data/impl/cube/cubeUtils';
import { useBlock } from '../../../../services/entities/hooks/useBlock';
import { UnitsEnum } from '../../../../services/entities/impl/blfApi/apiConstants';
import { useGetRanchQuery } from '../../../../services/entities/impl/blfApi/endpoints/ranches';
import { useGetScanByIdQuery } from '../../../../services/entities/impl/blfApi/endpoints/scans';
import { useTranslation } from '../../../../services/translation/hooks/useTranslation';
import CopyButton from '../../../commonUI/CopyButton';
import { flexCenter } from '../../../commonUI/cssClasses';
import useCurrServicesAndMeasures from '../../../customerSummary/hooks/useCurrServicesAndMeasures';
import DebugInfoSection from '../../../debugInfo/components/DebugInfoSection';
import ImageInfoSlot from '../../../imageViewer/ImageInfoSlot';
import { roundFractional } from '../../../ranches/ranchUtils';
import { useBlueberryFlowerFeatures } from '../../../scanDataApi/hooks/useBlueberryFlowerFeatures';
import useScanData from '../../../scanDataApi/hooks/useScanData';
import {
  OperatorEnum,
  operatorToExpressionMap,
  ScanDataFilter,
  useScanDataQuery,
} from '../../../scanDataApi/hooks/useScanDataQuery';
import { ScanDataEnum } from '../../../scanDataApi/scanDataDefinitions';
import {
  selectCurrBlockID,
  selectCurrFeature,
  selectCurrRanchID,
  selectCurrScanID,
} from '../../../select/selectSlice';
import { useUnits } from '../../../settings/hooks/useUnits';
import {
  selectMapMeasurePoint1,
  selectMapMeasurePoint2,
  setMapMeasurePoint1,
  setMapMeasurePoint2,
} from '../../mapSlice';
import {
  generateTSVFromArray,
  sortTimestampsAsString,
  transformToCopyPasteData,
} from '../../mapUtils';
import MapMeasureTableBlueberries from './MapMeasureTableBlueberries';
import SetPointsByID from './SetPointsByID';
import { calculateAverageVelocity } from './utils/calculateAverageVelocity';

export interface EstimatedCountSumQueryResult {
  totalCount: number;
  totalDistanceTraveled: number;
  avgVelocity: number;
  [ScanDataEnum.class_name]?: FeatureEnum;
}

// Build the query params
interface QueryParams {
  cols: string[];
  filters: ScanDataFilter[];
  filtersSQL: string[];
  group: ScanDataEnum[] | string[];
}

const MapMeasureDialogContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currScanID = useAppSelector(selectCurrScanID);
  const currFeature = useAppSelector(selectCurrFeature);
  const point1 = useAppSelector(selectMapMeasurePoint1);
  const point2 = useAppSelector(selectMapMeasurePoint2);
  const { currPointScanData } = useScanData();
  const { servicesAndMeasuresWithFeatureFilter } = useCurrServicesAndMeasures();
  const units = useUnits();
  const ranchID = useAppSelector(selectCurrRanchID);
  const blockID = useAppSelector(selectCurrBlockID);
  const scanID = useAppSelector(selectCurrScanID);

  const { currentData: ranchData, isLoading: isLoadingRanch } =
    useGetRanchQuery(ranchID, {
      skip: !ranchID || ranchID === '',
    });
  const ranch =
    !ranchID || ranchID === '' || isLoadingRanch ? undefined : ranchData;

  const { block: blockData, isBlockLoading } = useBlock(blockID);
  const block = !blockID || isBlockLoading ? undefined : blockData;

  const { currentData: scanData, isLoading: isLoadingScan } =
    useGetScanByIdQuery(scanID);
  const scan = !scanID || scanID === '' || isLoadingScan ? undefined : scanData;

  const isBlueberry = useMemo(() => {
    return (
      isBlueberryFeature(currFeature) || isBlueberryFlowerFeature(currFeature)
    );
  }, [currFeature]);

  const blueberryFlowerFeatures = useBlueberryFlowerFeatures();

  const handleSetPoint1 = () => {
    if (
      currPointScanData &&
      currPointScanData.data &&
      currPointScanData?.data.length > 0
    ) {
      dispatch(setMapMeasurePoint1(currPointScanData.data[0]));
    } else {
      dispatch(setMapMeasurePoint1(null));
      alert(t('Point not found.', { ns: 'ranch-view' }));
    }
  };

  const handleSetPoint2 = () => {
    if (
      currPointScanData &&
      currPointScanData.data &&
      currPointScanData?.data.length > 0
    ) {
      dispatch(setMapMeasurePoint2(currPointScanData.data[0]));
    } else {
      dispatch(setMapMeasurePoint1(null));
      alert(t('Point not found.', { ns: 'ranch-view' }));
    }
  };

  // Reset the points if the scan changes
  useEffect(() => {
    dispatch(setMapMeasurePoint1(null));
    dispatch(setMapMeasurePoint2(null));
  }, [dispatch, currScanID]);

  const queryParams = useMemo(() => {
    const queryParams: QueryParams = {
      cols: [],
      filters: [],
      filtersSQL: [],
      group: [],
    };

    if (!point1 || !point2) {
      return queryParams;
    }

    const measure = 'estimated_count';
    queryParams.cols = [
      `sum(${measure}) as totalCount`,
      `sum(${ScanDataEnum.raw_count}) as totalRawCount`,
      `sum(${ScanDataEnum.traveled_distance}) as totalDistanceTraveled`,
    ];

    if (isBlueberry) {
      queryParams.cols.push(ScanDataEnum.class_name);
    }

    // Since multiple cameras can be used in a scan, we need to filter by the camera ID of the first point so that we only get the data from the camera that was used to scan the points.  Other cameras may have timestamps in the same range but scanning a different area of the field.
    queryParams.filters.push({
      col: ScanDataEnum.point_camera_id,
      op: OperatorEnum.eq,
      value: point1.point_camera_id,
    });

    queryParams.filters.push({
      col: measure,
      op: OperatorEnum.neq,
      value: null,
    });

    queryParams.filters.push({
      col: ScanDataEnum.point_timestamp,
      op: OperatorEnum.between,
      value: sortTimestampsAsString([
        point1.point_timestamp,
        point2.point_timestamp,
      ]),
    });

    if (isBlueberry) {
      queryParams.filtersSQL.push(
        `${ScanDataEnum.class_name} IN (${[
          ...BlueberryFeatures,
          ...blueberryFlowerFeatures,
        ]
          .map((el) => `'${el}'`)
          .join(', ')})`
      );

      // For blueberries, we turn off the normal useScanData model, class, and version filters and build manually
      if (servicesAndMeasuresWithFeatureFilter.currService) {
        queryParams.filtersSQL.push(
          operatorToExpressionMap[OperatorEnum.eq](
            ScanDataEnum.model_name,
            servicesAndMeasuresWithFeatureFilter.currService[
              CubeDimensionEnum.modelName
            ]
          ),
          operatorToExpressionMap[OperatorEnum.eq](
            ScanDataEnum.model_version,
            servicesAndMeasuresWithFeatureFilter.currService[
              CubeDimensionEnum.modelVersion
            ]
          )
        );
      }
    }

    if (isBlueberry) {
      queryParams.group = [ScanDataEnum.class_name];
    }

    return queryParams;
  }, [
    point1,
    point2,
    currFeature,
    isBlueberry,
    servicesAndMeasuresWithFeatureFilter.currService,
  ]);

  const { data, isLoading, error } =
    useScanDataQuery<EstimatedCountSumQueryResult>({
      cols: queryParams.cols,
      filters: queryParams.filters,
      filtersSQL: queryParams.filtersSQL,
      group: queryParams.group,
      disableClassFilter: isBlueberry,
      skip: !point1 || !point2 || !currScanID,
      useUnitsTransformSubstringMatching: true,
    });

  let statusText = t('Select two points to measure.', { ns: 'ranch-view' });
  if (isLoading) {
    statusText = t('Loading...', { ns: 'ranch-view' });
  } else if (error) {
    statusText = t('Error', { ns: 'ranch-view' });
  }

  const resultsReady = !isLoading && !error;

  let content: JSX.Element | null = null;
  if (resultsReady && data && data.length > 0) {
    // Loop through the data and check if at least one of the points is a blueberry flower or blueberry feature
    const isBlueberry = data.some(
      (d) =>
        isBlueberryFeature(d?.class_name || null) ||
        isBlueberryFlowerFeature(d?.class_name || null)
    );

    // If it is a blueberry feature, we need to calculate the total distance traveled by finding the max distance traveled of any blueberry feature in the data bc the max distance traveled is the total distance traveled (some blueberry features may have null distance traveled values due to pipeline memory issues so whichever is the max most likely has the correct value with the least amount of nulls)
    const totalDistance = isBlueberry
      ? Math.max(...data.map((d) => d.totalDistanceTraveled))
      : data[0].totalDistanceTraveled;

    const avgVelocity = calculateAverageVelocity(
      point1,
      point2,
      totalDistance,
      units
    );

    const distanceRounded = roundFractional(totalDistance || 0, 2);
    const velocityRounded = roundFractional(avgVelocity || 0, 2);

    const traveledDistanceLabel = `${t(
      `Traveled Distance (${units === UnitsEnum.imperial ? 'ft' : 'm'})`,
      { ns: 'ranch-view' }
    )}: `;

    const velocityLabel = t(
      `Velocity (${units === UnitsEnum.imperial ? 'MPH' : 'KPH'})`,
      { ns: 'ranch-view' }
    );

    if (isBlueberry && data.length >= 4) {
      // Convert data to csv format for copy button
      const totalBerriesCount = data.reduce((acc, curr) => {
        if (isBlueberryFlowerFeature(curr?.class_name || null)) {
          return acc;
        }
        return acc + curr.totalCount;
      }, 0);

      const totalFlowersCount = data.reduce((acc, curr) => {
        if (isBlueberryFlowerFeature(curr?.class_name || null)) {
          return acc + curr.totalCount;
        }
        return acc;
      }, 0);

      const totalBerriesCountSum: EstimatedCountSumQueryResult = {
        [ScanDataEnum.class_name]: FeatureEnum.blueberry,
        totalCount: totalBerriesCount,
        totalDistanceTraveled: totalDistance,
        avgVelocity: avgVelocity || 0,
      };

      const totalFlowersCountSum: EstimatedCountSumQueryResult = {
        [ScanDataEnum.class_name]: FeatureEnum.flower,
        totalCount: totalFlowersCount,
        totalDistanceTraveled: totalDistance,
        avgVelocity: avgVelocity || 0,
      };

      const copyPasteData = transformToCopyPasteData([
        ...data,
        totalBerriesCountSum,
        totalFlowersCountSum,
      ]);

      const translatedCopyPasteData = copyPasteData.map((d) => {
        return {
          label: t(d.label),
          data: d.data,
        };
      });

      const tsvData = generateTSVFromArray([
        { label: t('Ranch'), data: ranch?.name || '' },
        { label: t('Block'), data: block?.name || '' },
        { label: t('Variety'), data: block?.variety?.name || '' },
        { label: t('Scan Date'), data: scan?.scanDate || '' },
        ...translatedCopyPasteData,
        { label: traveledDistanceLabel, data: distanceRounded },
        { label: velocityLabel, data: velocityRounded },
        { label: `${t('Point')} ID 1`, data: point1?.point_id || '' },
        { label: `${t('Point')} ID 2`, data: point2?.point_id || '' },
      ]);

      content = (
        <Stack
          direction='row'
          spacing={2}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <MapMeasureTableBlueberries
            blueberryData={data}
            avgVelocity={avgVelocity || 0}
          />
          <CopyButton textToCopy={tsvData} format='text/plain' />
        </Stack>
      );
    } else {
      const totalCount = data[0].totalCount;
      const countRounded = roundFractional(totalCount || 0, 0);

      content = (
        <Box sx={{ ...flexCenter, flexDirection: 'row' }}>
          <ImageInfoSlot
            label={`${t('Total Estimated Count', { ns: 'ranch-view' })}: `}
            children={
              <>
                {countRounded}
                <CopyButton textToCopy={countRounded} />
              </>
            }
          />
          <ImageInfoSlot
            label={traveledDistanceLabel}
            children={
              <>
                {distanceRounded}
                <CopyButton textToCopy={distanceRounded} />
              </>
            }
          />
          <ImageInfoSlot
            label={velocityLabel}
            children={
              <>
                {velocityRounded}
                <CopyButton textToCopy={velocityRounded} />
              </>
            }
          />
        </Box>
      );
    }
  } else {
    content = (
      <Typography variant='h6' sx={{ textAlign: 'center', p: '1rem' }}>
        {statusText}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: '30rem',
        ...flexCenter,
        flexDirection: 'column',
      }}
    >
      <Typography variant='subtitle1' sx={{ textAlign: 'center' }}>
        {t(
          'Measures the sum of the estimated count between two points (inclusive).',
          {
            ns: 'ranch-view',
          }
        )}
      </Typography>
      <Box sx={{ ...flexCenter, gap: '1rem', m: '1rem' }}>
        <Button
          variant='contained'
          onClick={handleSetPoint1}
          sx={{
            backgroundColor: cyan[500],
            '&:hover': {
              bgcolor: cyan[500],
              opacity: 0.8,
            },
          }}
        >
          {t('Set Point 1', { ns: 'ranch-view' })}
        </Button>
        <Button
          variant='contained'
          onClick={handleSetPoint2}
          sx={{
            backgroundColor: purple[500],
            '&:hover': {
              backgroundColor: purple[500],
              opacity: 0.8,
            },
          }}
        >
          {t('Set Point 2', { ns: 'ranch-view' })}
        </Button>
      </Box>
      <SetPointsByID />
      {point1 && (
        <DebugInfoSection
          sectionLabel={'Point 1'}
          name={''}
          id={point1?.point_id ?? ''}
          isLoading={false}
          jsonObjectOrArrayOfObjects={[point1]}
        />
      )}
      {point2 && (
        <DebugInfoSection
          sectionLabel={'Point 2'}
          name={''}
          id={point2?.point_id ?? ''}
          isLoading={false}
          jsonObjectOrArrayOfObjects={[point2]}
        />
      )}
      {content}
    </Box>
  );
};

export default MapMeasureDialogContent;
